//@ts-ignore
//libraries

import "../css/Website.scss";
import Navigation from "../components/navigation/Navigation";
import OutroCTA from "../components/OutroCTA";
import Footer from "../components/Footer";

const PrivacyStatement = () => {
  return (
    <div id="stippl-about">
      <Navigation impact={false} />
      <div style={{ paddingTop: "5rem", paddingBottom: "4rem" }}>
      <div className="container__full-width">
        <div className="container__inner">
          <div className="legal-container">
            <h1>Privacy Policy</h1>
            <p>
              <em>Stippl B.V., stippl.io · Last updated: October 2026 · Effective: October 2026</em>
            </p>

            <h2 id="who-we-are">1. Who We Are</h2>
            <p>
              This Privacy Policy explains how Stippl B.V. ("Stippl", "we", "us", "our") collects, uses, and protects
              your personal data when you use our website at{" "}
              <a href="https://www.stippl.io/" target="_blank" rel="noopener noreferrer">
                stippl.io
              </a>
              , our mobile applications, and related services
              (collectively, the "Service").
            </p>
            <p>Stippl B.V. is the data controller for personal data processed through the Service.</p>
            <p>
              <strong>Contact details:</strong>
              <br />
              Stippl B.V.
              <br />
              Ruimzicht 129
              <br />
              1068CS Amsterdam, the Netherlands
              <br />
              KvK: 82938865
              <br />
              Email: <a href="mailto:info@stippl.io">info@stippl.io</a>
            </p>
            <p>
              <strong>Data Protection Officer (DPO):</strong> We have not appointed a DPO as we do not meet the
              threshold requirements under Article 37 GDPR. Privacy enquiries can be directed to{" "}
              <a href="mailto:support@stippl.io">support@stippl.io</a>
              .
            </p>

            <h2 id="scope">2. Scope</h2>
            <p>
              This Policy applies to all personal data we process in connection with the Service, under the General
              Data Protection Regulation (GDPR) and Dutch law.
            </p>
            <p>
              <strong>Stippl for Business.</strong> If you use Stippl for Business, this Policy covers the personal
              data we process about you as an account holder and about your colleagues as authorised users. Personal
              data that you enter about your own clients ("Client Data") is processed by Stippl as a processor on your
              behalf; that relationship is governed by our{" "}
              <a href="/business-terms#data-protection">Business Terms (Section 9)</a>, including the{" "}
              <a href="/business-terms#sub-processors">sub-processor list</a> there, and, where agreed, a Data
              Processing Agreement, not by this Policy. Your clients receive their privacy information from you.
            </p>

            <h2 id="data-we-collect">3. Personal Data We Collect</h2>
            <h3>3.1 Data You Provide</h3>
            <ul>
              <li>
                <strong>Account:</strong> email address, name, password (stored as a hash), and optional profile
                information such as age, hometown, and a profile photo. If you sign in with Google, Apple, or Facebook,
                we receive your name and email address from them.
              </li>
              <li>
                <strong>Trips:</strong> itineraries, destinations, notes, journal entries, photos and videos, expenses,
                and anything else you add.
              </li>
              <li>
                <strong>Traveller preferences:</strong> optional preferences such as travel pace, budget, room
                preferences, dietary requirements, and special requests. Some of these (for example dietary
                requirements) may reveal information about your health or beliefs; we use them only to personalise
                your travel planning, including suggestions from our AI assistant.
              </li>
              <li>
                <strong>AI assistant:</strong> the messages you send and any images you share in the chat.
              </li>
              <li>
                <strong>Forwarded booking emails:</strong> the content and attachments of booking confirmations you
                forward to your trip's Stippl email address, used to add the booking to your trip.
              </li>
              <li>
                <strong>Bookings and purchases:</strong> for Stays bookings, eSIMs, photobooks, and subscriptions, the
                details needed to complete them (see <a href="#stays">Section 5</a> for Stays).
              </li>
              <li>
                <strong>People you invite:</strong> if you invite someone to a trip, we use their email address to send
                the invitation.
              </li>
              <li>
                <strong>Support and feedback:</strong> messages you send our support team and answers you give in
                surveys.
              </li>
              <li>
                <strong>Business contacts:</strong> if you contact us about Stippl for Business, your name, email
                address, company name, and website.
              </li>
            </ul>
            <h3>3.2 Data We Collect Automatically</h3>
            <ul>
              <li>
                <strong>Usage and device data:</strong> screens and pages visited, features used, search queries, IP
                address, device and browser type, and device identifiers.
              </li>
              <li>
                <strong>Location:</strong> approximate location from your IP address. With your permission, the app
                uses your device's location while it is open, to center the map and to find hotels near you; for that
                search it is sent to our booking partner. It is not saved to your account.
              </li>
              <li>
                <strong>Session replay:</strong> recordings of how the interface is used (taps, scrolls, screens), on
                the website with your consent and in the app for a sample of users (see{" "}
                <a href="#mobile-app">Section 12</a>).
              </li>
              <li>
                <strong>Email interaction:</strong> whether our emails are opened and which links are clicked.
              </li>
            </ul>
            <h3 id="visibility">3.3 What Others Can See</h3>
            <p>
              Each trip has its own visibility setting: trip members only, your followers, or everyone. Trips set to
              everyone appear on your profile, can be viewed by anyone with the link, and can appear in search engines,
              together with your display name, username, and profile photo. Trips set to your followers appear on your
              profile only for your followers, and trips set to members only do not appear on it; neither is shown to
              search engines. Whatever the setting, anyone who has the link to a trip can open it. Booking details are
              only shown to trip members; booking numbers in your notes are hidden from others. You can change the
              setting for each trip at any time.
            </p>

            <h3 id="chatgpt-claude">3.4 ChatGPT and Claude</h3>
            <p>
              You can connect your Stippl account to ChatGPT (OpenAI) or Claude (Anthropic) and ask the assistant to
              save or change trips in Stippl. When you do:
            </p>
            <ul>
              <li>
                <strong>What we receive:</strong> only what the assistant sends to save or change a trip: the trip name,
                start date, destinations, and the places, times and notes for each day. We do not receive your
                conversation.
              </li>
              <li>
                <strong>What the assistant receives:</strong> our answers to its requests: the names, dates and links of
                your trips, and which places were added. OpenAI and Anthropic handle this under their own privacy
                policies.
              </li>
              <li>
                <strong>Finding places:</strong> to find the places and destinations on the map, we send place and city
                names to Serper, LocationIQ and Google. No account data is sent.
              </li>
              <li>
                <strong>The connection itself:</strong> we keep a record of each connection (which assistant, when it was
                made and last used) and a hashed key that keeps the assistant signed in. The assistant loses access when
                you disconnect Stippl in its settings, change your Stippl password, or delete your account.
              </li>
            </ul>

            <h2 id="how-we-use">4. How We Use Your Personal Data</h2>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Purpose</th>
                  <th>Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Providing the Service: your account, trips, AI assistant, bookings, purchases, and subscriptions</td>
                  <td>Art. 6(1)(b), performance of contract</td>
                </tr>
                <tr>
                  <td>Service emails and notifications (confirmations, receipts, support, trip reminders)</td>
                  <td>Art. 6(1)(b), performance of contract / Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>
                    Emails about Stippl's features and offers; you can opt out at signup, in your settings, and in
                    every email
                  </td>
                  <td>Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>Personalising content and reminders based on your trips</td>
                  <td>Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>
                    Improving the Service: usage analytics, email open and click measurement, and testing different
                    versions of features and prices
                  </td>
                  <td>Art. 6(1)(f), legitimate interests / Art. 6(1)(a), consent for cookies</td>
                </tr>
                <tr>
                  <td>
                    Publishing anonymised travel statistics (for example typical trip lengths), never based on fewer
                    than 500 trips
                  </td>
                  <td>Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>
                    Reviewing support and AI assistant conversations for quality, including with AI tools, with names
                    and contact details removed where possible
                  </td>
                  <td>Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>Advertising measurement</td>
                  <td>
                    On the website: Art. 6(1)(a), consent. In the app: Art. 6(1)(f), legitimate interests, limited by
                    your device's tracking settings.
                  </td>
                </tr>
                <tr>
                  <td>Security, error monitoring, and preventing fraud and abuse</td>
                  <td>Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>Handling business leads and outreach</td>
                  <td>Art. 6(1)(f), legitimate interests</td>
                </tr>
                <tr>
                  <td>Complying with legal obligations (for example tax and requests from authorities)</td>
                  <td>Art. 6(1)(c), legal obligation</td>
                </tr>
              </tbody>
            </table>
            <p>
              You may object to processing based on legitimate interests at any time (see{" "}
              <a href="#your-rights">Section 9</a>).
            </p>

            <h2 id="stays">5. Stays Bookings</h2>
            <p>
              When you book accommodation through Stippl Stays, we share your name, date of birth (where required),
              nationality, contact details, billing address, and any preferences or accessibility requirements with
              Nuitee Travel Limited ("Nuitee", operator of the LiteAPI booking platform), the merchant of record, which
              passes them to the accommodation. Card details go encrypted directly to Nuitee's payment processor; Stippl
              does not store full card numbers. If you book for children, we pass on their ages. See{" "}
              <a href="https://nuitee.com/privacy-policy" target="_blank" rel="noopener noreferrer">
                Nuitee's Privacy Policy
              </a>
              .
            </p>

            <h2 id="data-sharing">6. Data Sharing</h2>
            <p>
              We do not sell your personal data. We share it only with the service providers below, who may use it
              only on our instructions, and with the parties listed under "Other". Providers marked "self-hosted" run on
              servers we control.
            </p>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>What for</th>
                  <th>Providers</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Bookings and payments</td>
                  <td>Stays bookings, payments, subscriptions, eSIMs, photobook printing</td>
                  <td>Nuitee (LiteAPI), Stripe, Apple App Store, Google Play, RevenueCat, Airalo, Cloudprinter</td>
                </tr>
                <tr>
                  <td>AI services</td>
                  <td>
                    AI assistant (including your trip details, preferences, and chat images), trip generation, booking
                    email import, reading the links, text and photos you add to find the places in them, quality
                    monitoring, internal conversation review
                  </td>
                  <td>Google (Gemini), Anthropic (Claude), Langfuse (self-hosted)</td>
                </tr>
                <tr>
                  <td>Maps and travel data</td>
                  <td>
                    Maps, routes, place search, weather, transport, hotels, activities, and reading the public social
                    media posts you add to a trip (only the post's link is sent)
                  </td>
                  <td>
                    Google Maps, Mapbox, Stadia Maps, LocationIQ, Serper, ScrapeCreators, and travel data providers (no
                    account data)
                  </td>
                </tr>
                <tr>
                  <td>Analytics and error monitoring</td>
                  <td>Understanding usage, session replay, crash and error reports</td>
                  <td>
                    Google Analytics, Firebase, Microsoft Clarity, Fathom, OpenPanel (self-hosted), Sentry (self-hosted)
                  </td>
                </tr>
                <tr>
                  <td>Advertising measurement</td>
                  <td>Measuring which campaigns bring visitors and app installs (with your consent where required)</td>
                  <td>Meta, Google Ads, Apple Search Ads, TikTok, Pinterest, OpenAI (ChatGPT ads), AppsFlyer</td>
                </tr>
                <tr>
                  <td>Email, notifications, and support</td>
                  <td>Sending emails and push notifications, receiving forwarded booking emails, support, surveys</td>
                  <td>Listmonk (hosted by Elestio), Resend, Google Workspace, Amazon Web Services, Expo, Crisp, Typeform</td>
                </tr>
                <tr>
                  <td>Hosting and storage</td>
                  <td>Servers, database, and file storage (mainly in the EU)</td>
                  <td>Fly.io, MongoDB Atlas, Cloudflare, Backblaze, Amazon Web Services, Hetzner</td>
                </tr>
                <tr>
                  <td>Business leads</td>
                  <td>Enriching and following up business enquiries</td>
                  <td>Anthropic (Claude), Slack, Lemlist</td>
                </tr>
                <tr>
                  <td>AI assistants you connect</td>
                  <td>
                    Only if you connect Stippl to them (see <a href="#chatgpt-claude">Section 3.4</a>): the names, dates
                    and links of your trips, and the places added
                  </td>
                  <td>OpenAI (ChatGPT), Anthropic (Claude)</td>
                </tr>
                <tr>
                  <td>Booking partners</td>
                  <td>
                    When you open a booking link (for example Booking.com, Expedia, Hostelworld, Skyscanner,
                    Rentalcars), the partner receives your visit and a reference so we are credited; your booking then
                    falls under the partner's own privacy policy
                  </td>
                  <td>The partner you choose</td>
                </tr>
                <tr>
                  <td>Other</td>
                  <td>Legal, accounting, and audit advice; legal requests; a merger or acquisition</td>
                  <td>Professional advisers, competent authorities, a successor entity</td>
                </tr>
              </tbody>
            </table>
            <p>
              Advertising and session-replay tools on the website load only after you accept marketing cookies (see{" "}
              <a href="#cookies">Section 7</a>). Each provider's own privacy policy applies to its processing.
            </p>

            <h2 id="cookies">7. Cookies</h2>
            <p>
              We use cookies and similar technologies. You can manage your preferences at any time through our cookie
              banner, which also names the providers in each category.
            </p>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Category</th>
                  <th>Purpose</th>
                  <th>Legal Basis</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Strictly Necessary</td>
                  <td>Essential for the Service to function (e.g., session management, security, bot protection)</td>
                  <td>Strictly necessary, no consent required</td>
                </tr>
                <tr>
                  <td>Functional</td>
                  <td>Remember your preferences (e.g., language, theme)</td>
                  <td>Art. 6(1)(a), consent</td>
                </tr>
                <tr>
                  <td>Analytics</td>
                  <td>Understand how you use the Service</td>
                  <td>Art. 6(1)(a), consent</td>
                </tr>
                <tr>
                  <td>Marketing</td>
                  <td>Advertising measurement and session replay</td>
                  <td>Art. 6(1)(a), consent</td>
                </tr>
              </tbody>
            </table>
            <p>
              Our emails contain a small image and tracked links that tell us whether an email was opened and which
              links were clicked. Withdrawing consent does not affect the lawfulness of processing before withdrawal.
            </p>

            <h2 id="transfers">8. International Data Transfers</h2>
            <p>
              Some providers in <a href="#data-sharing">Section 6</a> process data outside the European Economic Area,
              mainly in the United States. We then rely on an adequacy decision of the European Commission (including
              the EU-US Data Privacy Framework) or on Standard Contractual Clauses. You can request a copy of these
              safeguards at <a href="mailto:business@stippl.io">business@stippl.io</a>.
            </p>

            <h2 id="your-rights">9. Your Rights</h2>
            <p>
              You have the right to access, correct, delete, restrict, and receive a copy of your personal data, to
              object to processing based on legitimate interests or for direct marketing, to withdraw consent at any
              time, and not to be subject to decisions based solely on automated processing that significantly affect
              you. You can delete your account yourself in the app or on the website.
            </p>
            <p>
              To exercise these rights, contact <a href="mailto:support@stippl.io">support@stippl.io</a>. We respond within one
              month (extendable by two months for complex requests, with notice to you). You can also lodge a complaint
              with the{" "}
              <a href="https://autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer">
                Dutch Data Protection Authority (Autoriteit Persoonsgegevens)
              </a>{" "}
              or the supervisory authority in your country.
            </p>

            <h2 id="retention">10. Data Retention</h2>
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Retention period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Account, trips, journals, photos, and AI assistant conversations</td>
                  <td>Until you delete them or your account; deleted trips stay in the trash for 30 days</td>
                </tr>
                <tr>
                  <td>Forwarded booking emails</td>
                  <td>
                    The original email: 7 days after it arrives. The booking documents created from it stay with your trip
                    until you delete them or the trip
                  </td>
                </tr>
                <tr>
                  <td>Payments, invoices, bookings, and orders</td>
                  <td>7 years (legal obligation)</td>
                </tr>
                <tr>
                  <td>Support conversations</td>
                  <td>2 years after your last contact</td>
                </tr>
                <tr>
                  <td>Analytics events and session recordings</td>
                  <td>Up to 14 months</td>
                </tr>
                <tr>
                  <td>Your name, email and other profile details in our analytics</td>
                  <td>Until you delete your account</td>
                </tr>
                <tr>
                  <td>Technical logs, error reports, and AI quality monitoring</td>
                  <td>Up to 90 days</td>
                </tr>
                <tr>
                  <td>Email open and click data</td>
                  <td>90 days</td>
                </tr>
                <tr>
                  <td>ChatGPT and Claude connections</td>
                  <td>
                    90 days after the connection was last used, 365 days at most, or straight away when you delete your
                    account
                  </td>
                </tr>
                <tr>
                  <td>Marketing preferences</td>
                  <td>While you are subscribed; a record of your consent or unsubscribe for 3 years after</td>
                </tr>
              </tbody>
            </table>
            <p>
              Deleted data can remain in our backups for up to 12 months before it is overwritten. We keep data longer
              only where we need it to comply with a legal obligation or to establish, exercise or defend a legal claim,
              for example a payment dispute, and only for as long as that lasts. After the retention period, data is
              deleted or anonymised.
            </p>

            <h2 id="fan-page">11. Facebook Page, Joint Controller</h2>
            <p>
              For visitor statistics (Facebook Insights) on our{" "}
              <a href="https://www.facebook.com/stippl/" target="_blank" rel="noopener noreferrer">
                Facebook page
              </a>
              , Stippl and Meta Platforms Ireland Limited are joint controllers under Article 26 GDPR, based on our
              legitimate interest in understanding our audience. Meta collects this data through its own cookies under{" "}
              <a href="https://www.facebook.com/privacy/policy" target="_blank" rel="noopener noreferrer">
                Meta's Privacy Policy
              </a>
              . You can exercise your rights with us at <a href="mailto:support@stippl.io">support@stippl.io</a> or with Meta
              directly.
            </p>

            <h2 id="mobile-app">12. Mobile App</h2>
            <ul>
              <li>
                <strong>Advertising measurement:</strong> in the app, advertising measurement is based on legitimate
                interests and limited by your device's tracking settings. If you decline Apple's App Tracking
                Transparency prompt, advertising identifiers are not used for ad measurement. Analytics that do not use
                them keep running.
              </li>
              <li>
                <strong>Session replay:</strong> a sample of app sessions is recorded (screens, taps, scrolls) to find
                usability problems, linked to your user ID.
              </li>
              <li>
                <strong>Permissions:</strong> location and push notifications are used only after you allow them in
                your device settings, where you can also switch them off.
              </li>
            </ul>

            <h2 id="children">13. Children's Privacy</h2>
            <p>
              The Service is not directed to children under the age of 16. We do not knowingly collect personal data
              from children under 16. If you become aware that a child has provided us with personal data without
              parental consent, please contact <a href="mailto:support@stippl.io">support@stippl.io</a> and we will take steps to remove the data.
            </p>
            <p>
              For Stays bookings, you must be at least 18 years old. Additional guest data for minors (e.g., ages)
              collected in the course of a booking is processed solely for the purpose of fulfilling that booking.
            </p>

            <h2 id="security">14. Security</h2>
            <p>
              We protect your personal data with appropriate technical and organisational measures, including
              encryption in transit and at rest and access controls. No system is completely secure. If you believe
              your Account has been compromised, please contact <a href="mailto:support@stippl.io">support@stippl.io</a>{" "}
              immediately.
            </p>

            <h2 id="changes">15. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. If we make material changes, we will notify you by
              email or through a prominent notice within the Service before the changes take effect. The "Last updated"
              date at the top of this page will always reflect the most recent version.
            </p>

            <h2 id="contact">16. Contact Us</h2>
            <p>For any privacy-related questions, requests, or complaints:</p>
            <p>
              Stippl B.V.
              <br />
              Attn: Privacy Team
              <br />
              Ruimzicht 129, 1068CS Amsterdam, the Netherlands
              <br />
              Email: <a href="mailto:support@stippl.io">support@stippl.io</a>
              <br />
              Website:{" "}
              our{" "}<a href="https://www.stippl.io/contact" target="_blank" rel="noopener noreferrer">
                contact page
              </a>
            </p>
            <p>We aim to respond to all privacy-related enquiries within 10 business days.</p>

            <p>
              <em>© 2026 Stippl B.V. All rights reserved.</em>
            </p>
          </div>
        </div>
      </div>
      {/* <Footer /> */}
      </div>
      <OutroCTA />
      <Footer />
    </div>
  );
};

export default PrivacyStatement;
